import { useEffect, useState } from 'react'
import type { Sprint, TaakPrioriteit, TaakSoort, TaakStatus } from '../../../gedeeld/types'
import { api } from '../../lib/api'
import { PRIORITEITEN, SOORTEN, STATUSSEN, sleutelVan, vanInvoer } from '../../lib/opmaak'
import { meld, meldFout, nieuweTaak, openTaak, taakGewijzigd, useBasis } from '../../lib/staat'
import { Kies, KiesMens, Knop, Veld, Venster } from '../ui'
import { PrioriteitIcoon, SoortIcoon } from './stukjes'

/* ===========================================================================
 *  Een nieuwe taak
 *
 *  Alleen de titel is verplicht. Wat je verder invult kun je later in de
 *  lade aanpassen; een venster met twintig verplichte velden is een venster
 *  dat mensen overslaan door het werk in een chat te zetten.
 *
 *  "Nog een" laat het venster open met dezelfde instellingen, voor wie na
 *  een overleg tien taken achter elkaar invoert.
 * =========================================================================== */

export default function NieuweTaak({
  projectId: startProject, status: startStatus, sprintId: startSprint,
}: { projectId?: string | null; status?: string; sprintId?: string | null }) {
  const { mensen, projecten } = useBasis()
  const actief = projecten.filter((p) => !p.archief)

  const [titel, setTitel] = useState('')
  const [omschrijving, setOmschrijving] = useState('')
  const [projectId, setProjectId] = useState<string>(startProject ?? '')
  const [soort, setSoort] = useState<TaakSoort>('taak')
  const [status, setStatus] = useState<TaakStatus>((startStatus as TaakStatus) ?? 'te_doen')
  const [prioriteit, setPrioriteit] = useState<TaakPrioriteit>('normaal')
  const [wie, setWie] = useState<string | null>(null)
  const [deadline, setDeadline] = useState('')
  const [punten, setPunten] = useState('')
  const [sprints, setSprints] = useState<Sprint[]>([])
  const [sprintId, setSprintId] = useState<string>(startSprint ?? '')
  const [nogEen, setNogEen] = useState(false)
  const [bezig, setBezig] = useState(false)

  useEffect(() => {
    if (!projectId) { setSprints([]); return }
    api.project(projectId).then((p) => setSprints(p.sprints.filter((s) => s.status !== 'afgerond'))).catch(() => setSprints([]))
  }, [projectId])

  const sluit = () => nieuweTaak(null)
  const gekozen = mensen.find((m) => m.id === wie)

  async function maak() {
    if (!titel.trim() || bezig) return
    setBezig(true)
    try {
      const t = await api.taakOpslaan({
        titel: titel.trim(),
        omschrijving: omschrijving.trim() || undefined,
        projectId: projectId || null,
        soort, status, prioriteit,
        toegewezenAan: wie,
        deadline: vanInvoer(deadline),
        punten: punten === '' ? null : Number(punten),
        sprintId: sprintId || null,
      })
      taakGewijzigd(t)
      const sleutel = sleutelVan(t, projecten)
      meld(`${sleutel ? sleutel + ' ' : ''}aangemaakt${t.toegewezenNaam ? ` en aan ${t.toegewezenNaam} gegeven` : ''}`)
      if (nogEen) {
        setTitel('')
        setOmschrijving('')
      } else {
        sluit()
        openTaak(t.id)
      }
    } catch (e) {
      meldFout(e)
    }
    setBezig(false)
  }

  return (
    <Venster titel="Nieuwe taak" onSluit={sluit} breed={640} bewustSluiten={!!titel || !!omschrijving}
      voet={<>
        <label className="vinkje">
          <input type="checkbox" checked={nogEen} onChange={(e) => setNogEen(e.target.checked)} />
          Nog een maken
        </label>
        <span className="vul" />
        <Knop soort="stil" onClick={sluit}>Annuleren</Knop>
        <Knop soort="hoofd" bezig={bezig} disabled={!titel.trim()} onClick={() => void maak()}>Aanmaken</Knop>
      </>}>
      <div className="formulier">
        <Veld label="Titel" breed>
          <input className="invoer groot" autoFocus value={titel} onChange={(e) => setTitel(e.target.value)}
            placeholder="Wat moet er gebeuren? Begin met een werkwoord."
            onKeyDown={(e) => { if (e.key === 'Enter') void maak() }} />
        </Veld>

        <Veld label="Omschrijving" breed>
          <textarea className="invoer" rows={4} value={omschrijving} onChange={(e) => setOmschrijving(e.target.value)}
            placeholder="Mag leeg. Markdown werkt: **vet**, - lijstjes, - [ ] vinkjes." />
        </Veld>

        <Veld label="Project">
          <Kies<string> waarde={projectId}
            opties={[{ key: '', label: 'Geen project' }, ...actief.map((p) => ({ key: p.id, label: p.naam, kleur: p.kleur }))]}
            onKies={(p) => { setProjectId(p); setSprintId('') }} />
        </Veld>

        <Veld label="Soort">
          <Kies<TaakSoort> waarde={soort} onKies={setSoort}
            opties={SOORTEN.map((s) => ({ key: s.key, label: s.label, icoon: <SoortIcoon soort={s.key} /> }))} />
        </Veld>

        <Veld label="Toegewezen aan" hulp={gekozen && !gekozen.zietWerk
          ? `${gekozen.naam} mag niet in Projects; er komt alleen een seintje.`
          : gekozen ? `${gekozen.naam} ziet de taak bij Mijn werk, met een seintje erbij.` : undefined}>
          <KiesMens waarde={wie} mensen={mensen} onKies={(m) => setWie(m?.id ?? null)} />
        </Veld>

        <Veld label="Prioriteit">
          <Kies<TaakPrioriteit> waarde={prioriteit} onKies={setPrioriteit}
            opties={PRIORITEITEN.map((p) => ({ key: p.key, label: p.label, icoon: <PrioriteitIcoon p={p.key} /> }))} />
        </Veld>

        <Veld label="Kolom">
          <Kies<TaakStatus> waarde={status} onKies={setStatus}
            opties={STATUSSEN.map((s) => ({ key: s.key, label: s.label, kleur: s.kleur }))} />
        </Veld>

        <Veld label="Deadline">
          <input type="date" className="invoer" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
        </Veld>

        <Veld label="Story points">
          <input type="number" min={0} max={100} className="invoer" value={punten} onChange={(e) => setPunten(e.target.value)} placeholder="—" />
        </Veld>

        {!!sprints.length && (
          <Veld label="Sprint">
            <Kies<string> waarde={sprintId} onKies={setSprintId}
              opties={[{ key: '', label: 'Backlog' }, ...sprints.map((s) => ({ key: s.id, label: s.naam + (s.status === 'actief' ? ' (loopt)' : '') }))]} />
          </Veld>
        )}
      </div>
    </Venster>
  )
}
